import { createDocument, type Editor } from "@tiptap/core";
import type { Mark, Node } from "@tiptap/pm/model";
import { closeHistory } from "@tiptap/pm/history";
import { TextSelection, EditorState, type Transaction } from "@tiptap/pm/state";
import { ChangeSet, type TokenEncoder } from "@tiptap/pm/changeset";
import { Mapping, StepMap, Transform } from "@tiptap/pm/transform";
import { withColumnWidths } from "./tableColumnWidths";
import { emptySummaryReview, mapSummaryReview, reviewTransaction, summaryReviewKey, summaryStreamTransaction, summaryTargetRanges, type ReviewRange } from "./summaryEditorReview";
import { keepSummaryReadingPosition as keepReadingPosition } from "./summaryEditorViewport";

/** Replace the smallest changed range, preserving untouched nodes and selection mapping. */
export function summaryDocumentTransaction(state: EditorState, document: Node): Transaction | null {
  const start = state.doc.content.findDiffStart(document.content);
  if (start === null) return null;
  const end = state.doc.content.findDiffEnd(document.content)!;
  const overlap = start - Math.min(end.a, end.b);
  if (overlap > 0) { end.a += overlap; end.b += overlap; }
  // A single structural replacement can span several independent edits. Map the
  // caret through the actual diffs so unchanged text between them keeps its selection.
  const keepSelection = (transaction: Transaction) => transaction.setSelection(
    state.selection.getBookmark().map(documentMapping(state.doc, transaction.doc)).resolve(transaction.doc));
  try {
    const transaction = state.tr.replace(start, end.a, document.slice(start, end.b));
    // ProseMirror's fitter can reshape open slices crossing table/field boundaries.
    if (transaction.doc.eq(document)) return keepSelection(transaction);
  } catch { /* Use closed block boundaries for structural changes. */ }
  let prefix = 0;
  let from = 0;
  while (prefix < Math.min(state.doc.childCount, document.childCount) && state.doc.child(prefix).eq(document.child(prefix))) {
    from += state.doc.child(prefix++).nodeSize;
  }
  let oldEnd = state.doc.content.size;
  let newEnd = document.content.size;
  let suffix = 0;
  while (suffix < Math.min(state.doc.childCount, document.childCount) - prefix &&
    state.doc.child(state.doc.childCount - suffix - 1).eq(document.child(document.childCount - suffix - 1))) {
    oldEnd -= state.doc.child(state.doc.childCount - suffix - 1).nodeSize;
    newEnd -= document.child(document.childCount - suffix - 1).nodeSize;
    suffix++;
  }
  return keepSelection(state.tr.replaceWith(from, oldEnd, document.content.cut(from, newEnd)));
}

// Include formatting and node attributes: the default changeset encoder ignores both.
const markTokens = new WeakMap<readonly Mark[], string>();
const nodeTokens = new WeakMap<Node, string>();
const encoder: TokenEncoder<string | number> = {
  encodeCharacter(char, marks) {
    if (!marks.length) return char;
    let token = markTokens.get(marks);
    if (!token) { token = JSON.stringify(marks.map(mark => mark.toJSON())); markTokens.set(marks, token); }
    return `${char}:${token}`;
  },
  encodeNodeStart(node) {
    let token = nodeTokens.get(node);
    if (!token) { token = JSON.stringify([node.type.name, node.attrs, node.marks.map(mark => mark.toJSON())]); nodeTokens.set(node, token); }
    return token;
  },
  encodeNodeEnd: node => `/${node.type.name}`,
  compareTokens: (a, b) => a === b,
};
// Widths are rounded when converting px to Markdown percentages and back.
// Review and passage locks compare content; merging/history retain exact attrs.
const reviewEncoder: TokenEncoder<string | number> = { ...encoder,
  encodeNodeStart(node) {
    if (!('colwidth' in node.attrs)) return encoder.encodeNodeStart(node);
    const { colwidth, ...attrs } = node.attrs;
    return JSON.stringify([node.type.name, attrs, node.marks.map(mark => mark.toJSON())]);
  },
};
function flatDocumentChanges(before: Node, after: Node, tokenEncoder = encoder) {
  const from = before.content.findDiffStart(after.content);
  if (from === null) return [];
  const end = before.content.findDiffEnd(after.content)!;
  const overlap = from - Math.min(end.a, end.b);
  if (overlap > 0) { end.a += overlap; end.b += overlap; }
  return ChangeSet.create(before, undefined, tokenEncoder)
    .addSteps(after, [new StepMap([from, end.a - from, end.b - from])], null).changes;
}
function documentChanges(before: Node, after: Node, tokenEncoder = encoder): { fromA: number; toA: number; fromB: number; toB: number }[] {
  // Keep independent paragraph/table updates separate, even when a token diff would join them.
  if (before.childCount !== after.childCount) return [...flatDocumentChanges(before, after, tokenEncoder)];
  let offsetA = 0, offsetB = 0;
  const changes = [];
  for (let index = 0; index < before.childCount; index++) {
    const a = before.child(index), b = after.child(index);
    if (!a.eq(b)) {
      for (const change of flatDocumentChanges(before.type.create(null, a), after.type.create(null, b), tokenEncoder)) {
        changes.push({ fromA: change.fromA + offsetA, toA: change.toA + offsetA, fromB: change.fromB + offsetB, toB: change.toB + offsetB });
      }
    }
    offsetA += a.nodeSize; offsetB += b.nodeSize;
  }
  return changes;
}
/** Every v3 snapshot is compared with the immutable original, never the previous preview. */
export function summarySnapshotReview(before: Node, after: Node, provisional = false) {
  // Tiptap appends an empty paragraph after headings/tables; that is not an AI edit.
  const withoutTrailingEmptyParagraph = (doc: Node) => doc.childCount > 1 && doc.lastChild?.type.name === "paragraph" && !doc.lastChild.content.size
    ? doc.copy(doc.content.cut(0, doc.content.size - doc.lastChild.nodeSize)) : doc;
  const changes = documentChanges(withoutTrailingEmptyParagraph(before), withoutTrailingEmptyParagraph(after), reviewEncoder)
    // Missing text in an incomplete snapshot can still arrive in a later chunk.
    .filter(change => !provisional || change.fromB !== change.toB)
    .map((change, index) => ({ id: index + 1, from: change.fromB, to: change.toB, deleted: change.fromB === change.toB,
      deletedText: change.fromB === change.toB ? before.textBetween(change.fromA, change.toA, "\n") : undefined }));
  return { ...emptySummaryReview(), visible: changes.length > 0, changes, nextId: changes.length + 1 };
}

function documentMapping(before: Node, after: Node, tokenEncoder = encoder) {
  return new Mapping([new StepMap(documentChanges(before, after, tokenEncoder).flatMap(change =>
    [change.fromA, change.toA - change.fromA, change.toB - change.fromB]))]);
}
type Range = { from: number; to: number };
export interface SummaryStreamCheckpoint {
  aiDocument: object;
  document: object;
  recoveryDocument: object;
  protectedRanges: Range[];
  mapping: { ranges: number[]; mirror?: number }[];
}
function blocksAround(doc: Node, from: number, to: number): Range {
  const start = doc.resolve(from), end = doc.resolve(to);
  return { from: start.depth ? start.before(1) : from, to: end.depth ? end.after(1) : to };
}
const mapRange = (range: Range, mapping: Mapping): Range => ({ from: mapping.map(range.from, -1), to: mapping.map(range.to, 1) });
const mapOwnedRange = (range: Range, mapping: Mapping): Range => {
  const from = mapping.map(range.from, 1);
  return { from, to: Math.max(from, mapping.map(range.to, -1)) };
};
function mapIncomingRange(range: Range, mapping: Mapping): Range {
  // A concurrent insertion is added after the user's text at that same source position.
  // Replacements exclude user insertions at either boundary instead of swallowing them.
  const from = mapping.map(range.from, 1);
  return { from, to: range.from === range.to ? from : mapping.map(range.to, -1) };
}
const touches = (a: Range, b: Range) => a.from <= b.to && a.to >= b.from;
const conflicts = (owned: Range, change: Range, insertion = false) => owned.from === owned.to ? !insertion && touches(owned, change)
  : change.from === change.to ? change.from > owned.from && change.from < owned.to
  : change.from < owned.to && change.to > owned.from;

function includeTextblockEdges(doc: Node, range: Range): Range {
  const from = doc.resolve(range.from), to = doc.resolve(range.to);
  return {
    from: from.parent.isTextblock && from.parentOffset === 0 ? from.before() : range.from,
    to: to.parent.isTextblock && to.parentOffset === to.parent.content.size ? to.after() : range.to,
  };
}
/** Merge streamed changes around the user's exact edits, including within the same block. */
export function createSummaryStreamEdit(editor: Editor, preserveExistingContent = false, restored?: SummaryStreamCheckpoint) {
  let baseline = restored ? EditorState.create({ schema: editor.schema,
    doc: editor.schema.nodeFromJSON(restored.recoveryDocument), plugins: editor.state.plugins }) : editor.state;
  // A generation retry can start with recovered user notes. They are owned
  // content, while the new AI stream still starts from an empty document.
  let aiDocument = restored ? editor.schema.nodeFromJSON(restored.aiDocument)
    : preserveExistingContent ? createDocument('<p></p>', editor.schema) : editor.state.doc;
  let protectedRanges: Range[] = restored ? restored.protectedRanges.map(range => ({ ...range }))
    : preserveExistingContent ? [{ from: 0, to: Math.max(0, editor.state.doc.content.size - 1) }] : [];
  if (restored) {
    baseline.doc.check(); aiDocument.check(); editor.state.doc.check();
    if (!editor.state.doc.eq(editor.schema.nodeFromJSON(restored.document)) || protectedRanges.some(range =>
      !Number.isInteger(range.from) || !Number.isInteger(range.to) || range.from < 0 || range.to < range.from || range.to > editor.state.doc.content.size)) {
      throw new Error('Invalid stream checkpoint');
    }
  }
  let applying = false;
  let finished = false;
  let hasUserChanges = !!restored || preserveExistingContent;
  let hasUserSelection = false;
  let baselineMapping: Mapping | undefined;
  // Keep the actual user transaction maps until the next snapshot. A fresh
  // text diff can place an insertion at the wrong repeated letter/word.
  let aiMapping: Mapping | undefined;
  if (restored) {
    aiMapping = new Mapping();
    for (const map of restored.mapping) aiMapping.appendMap(new StepMap(map.ranges), map.mirror);
  }
  let reviewing = false;
  let release: ReviewRange[] = [];
  const dispatch = (transaction: Transaction) => {
    transaction.setMeta(summaryStreamTransaction, true);
    const before = editor.state.doc;
    const previousReview = summaryReviewKey.getState(editor.state);
    applying = true;
    try {
      editor.view.dispatch(transaction);
      if (reviewing && !finished && (transaction.docChanged || release.length)) {
        const after = editor.state.doc;
        // Layout-only changes must not collapse existing markers onto cell edges.
        const mapping = documentMapping(before, after, reviewEncoder);
        editor.view.dispatch(reviewTransaction(editor.state.tr, {
          base: previousReview && mapSummaryReview(previousReview, mapping),
          release: release.map(range => mapRange(range, mapping)),
          changes: documentChanges(before, after, reviewEncoder).map(change => ({ from: change.fromB, to: change.toB,
            deleted: change.fromB === change.toB,
            deletedText: change.fromB === change.toB ? before.textBetween(change.fromA, change.toA, "\n") : undefined })),
        }));
      }
    }
    finally { applying = false; }
  };
  const replace = (document: Node, history: boolean) => {
    const transaction = summaryDocumentTransaction(editor.state, document);
    if (transaction) dispatch(history ? closeHistory(transaction) : transaction.setMeta("addToHistory", false));
  };
  const previewDocument = (next: Node, returnedTarget?: Node) => {
    if (finished) return;
    release = reviewing && returnedTarget ? summaryTargetRanges(aiDocument, returnedTarget).map(range =>
      mapRange(includeTextblockEdges(aiDocument, range), aiMapping ?? documentMapping(aiDocument, editor.state.doc))) : [];
    const before = editor.state.doc;
    keepReadingPosition(editor, () => {
      if (!protectedRanges.length) replace(next, false);
      else {
        const mapping = aiMapping ?? documentMapping(aiDocument, editor.state.doc);
        const transaction = editor.state.tr;
        for (const change of [...documentChanges(aiDocument, next)].reverse()) {
          const insertion = change.fromA === change.toA;
          const range = mapIncomingRange(mapIncomingRange({ from: change.fromA, to: change.toA }, mapping), transaction.mapping);
          const protectedNow = protectedRanges.map(item => mapOwnedRange(item, transaction.mapping));
          if (range.from > range.to || protectedNow.some(item => conflicts(item, range, insertion))) continue;
          try {
            const candidate = new Transform(transaction.doc).replace(range.from, range.to, next.slice(change.fromB, change.toB));
            // A structural fitter may expand the requested range. Never let that touch owned text.
            const affected = documentChanges(transaction.doc, candidate.doc);
            if (affected.some(item => protectedNow.some(owned => conflicts(owned, { from: item.fromA, to: item.toA }, insertion)))) continue;
            for (const step of candidate.steps) transaction.step(step);
          } catch { /* Ambiguous structural overlap: retain the user's visible passage. */ }
        }
        if (transaction.docChanged) {
          const selection = editor.state.selection;
          if (selection instanceof TextSelection) {
            // Keep typing before newly arriving text at the cursor, rather than chasing the stream.
            transaction.setSelection(TextSelection.between(transaction.doc.resolve(transaction.mapping.map(selection.anchor, -1)),
              transaction.doc.resolve(transaction.mapping.map(selection.head, -1))));
          }
          dispatch(transaction.setMeta("addToHistory", false));
        }
      }
    });
    if (release.length && before.eq(editor.state.doc)) dispatch(editor.state.tr);
    release = [];
    aiDocument = next;
    aiMapping = undefined;
  };
  return {
    /** Called for every editor transaction, including selection and native undo/redo. */
    get applying() { return applying; },
    get recoveryDocument() { return baseline.doc; },
    get hasUserChanges() { return hasUserChanges; },
    /** Capture immutable nodes now; serialize only at the batched local-save boundary. */
    captureCheckpoint() {
      const ai = aiDocument, document = editor.state.doc, recovery = baseline.doc;
      const ranges = protectedRanges.map(range => ({ ...range }));
      const mapping = aiMapping ?? documentMapping(ai, document);
      const maps = mapping.maps.map((map, index) => {
        const ranges: number[] = [];
        map.forEach((from, to, newFrom, newTo) => ranges.push(from, to - from, newTo - newFrom));
        const mirror = mapping.getMirror(index);
        return { ranges, mirror: mirror !== undefined && mirror < index ? mirror : undefined };
      });
      return (): SummaryStreamCheckpoint => ({ aiDocument: ai.toJSON(), document: document.toJSON(),
        recoveryDocument: recovery.toJSON(), protectedRanges: ranges, mapping: maps });
    },
    recordTransaction(transaction: Transaction) {
      if (finished) return;
      protectedRanges = protectedRanges.map(range => mapOwnedRange(range, transaction.mapping));
      if (!applying && transaction.selectionSet) hasUserSelection = true;
      if (applying) { if (transaction.docChanged) baselineMapping = undefined; return; }
      if (!transaction.docChanged) return;
      aiMapping ??= documentMapping(aiDocument, transaction.before);
      aiMapping.appendMapping(transaction.mapping);
      const start = transaction.before.content.findDiffStart(transaction.doc.content);
      if (start === null) return;
      const end = transaction.before.content.findDiffEnd(transaction.doc.content)!;
      const oldRange = blocksAround(transaction.before, start, Math.max(start, end.a));
      const newRange = blocksAround(transaction.doc, transaction.mapping.map(oldRange.from, -1), transaction.mapping.map(oldRange.to, 1));
      const beforeMapping = baselineMapping ?? documentMapping(transaction.before, baseline.doc);
      const mapped = mapRange(oldRange, beforeMapping);
      const baselineRange = blocksAround(baseline.doc, mapped.from, mapped.to);
      const target = new Transform(baseline.doc).replaceWith(baselineRange.from, baselineRange.to, transaction.doc.content.cut(newRange.from, newRange.to)).doc;
      const baselineChange = summaryDocumentTransaction(baseline, target) ?? baseline.tr;
      // Reuse transaction maps while typing; re-diff only after another AI preview.
      baselineMapping = new Mapping();
      baselineMapping.appendMappingInverted(transaction.mapping);
      baselineMapping.appendMapping(beforeMapping);
      baselineMapping.appendMapping(baselineChange.mapping);
      baselineChange.setSelection(transaction.selection.getBookmark().map(baselineMapping).resolve(baselineChange.doc));
      if (!hasUserChanges) closeHistory(baselineChange);
      baseline = baseline.apply(baselineChange);
      // Use the actual edited range. Diffing "Een" against "Eigen Een"
      // incorrectly protects a repeated source letter as part of the insertion.
      transaction.steps.forEach((step, index) => {
        const affected: Range[] = [];
        step.getMap().forEach((_from, _to, from, to) => affected.push({ from, to }));
        if (!affected.length) {
          const json = step.toJSON();
          if (typeof json.from === "number" && typeof json.to === "number") affected.push({ from: json.from, to: json.to });
          else if (typeof json.pos === "number") affected.push({ from: json.pos, to: json.pos + (transaction.docs[index].nodeAt(json.pos)?.nodeSize ?? 1) });
        }
        protectedRanges.push(...affected.map(range => mapOwnedRange(range, transaction.mapping.slice(index + 1))));
      });
      // Coalesce adjacent edits, without claiming untouched text elsewhere in the same block.
      protectedRanges.sort((a, b) => a.from - b.from);
      protectedRanges = protectedRanges.reduce<Range[]>((ranges, range) => {
        const last = ranges[ranges.length - 1];
        if (last && touches(last, range)) last.to = Math.max(last.to, range.to);
        else ranges.push({ ...range });
        return ranges;
      }, []);
      hasUserChanges = true;
    },
    previewDocument,
    startReview(range: ReviewRange, scope: "selection" | "document") {
      reviewing = true;
      editor.view.dispatch(reviewTransaction(editor.state.tr, { start: range, scope }));
    },
    lockTarget(html: string) {
      if (!reviewing || finished) return;
      const mapping = aiMapping ?? documentMapping(aiDocument, editor.state.doc);
      const lock = summaryTargetRanges(aiDocument, createDocument(html, editor.schema)).map(range => mapRange(range, mapping))
        .filter(range => !protectedRanges.some(owned => conflicts(owned, range)));
      if (lock.length) editor.view.dispatch(reviewTransaction(editor.state.tr, { lock }));
    },
    /** V3 snapshots stay separate; lock only source passages actually addressed by a preview. */
    lockSnapshotTargets(html: string) {
      if (!reviewing || finished) return;
      const mapping = aiMapping ?? documentMapping(aiDocument, editor.state.doc);
      const lock = documentChanges(aiDocument, createDocument(html, editor.schema), reviewEncoder)
        .filter(change => change.fromB !== change.toB && change.fromA !== change.toA)
        .map(change => mapRange({ from: change.fromA, to: change.toA }, mapping))
        .filter(range => !protectedRanges.some(owned => conflicts(owned, range)));
      if (lock.length) editor.view.dispatch(reviewTransaction(editor.state.tr, { lock }));
    },
    releaseLocks() { if (reviewing) editor.view.dispatch(reviewTransaction(editor.state.tr, { finish: true })); },
    setSource(html: string) { aiDocument = createDocument(html, editor.schema); },
    preview(html: string, widths: (string | null)[][] = [], returnedTarget?: string) {
      if (finished) return;
      const tables = Array.from(editor.view.dom.querySelectorAll("table"));
      const next = withColumnWidths(createDocument(html, editor.schema), widths,
        widths.map((_, index) => tables[index]?.offsetWidth || editor.view.dom.clientWidth));
      previewDocument(next, returnedTarget === undefined ? undefined : createDocument(returnedTarget, editor.schema));
    },
    commit() {
      if (finished) return;
      finished = true;
      const finalDocument = editor.state.doc;
      const selection = editor.state.selection.getBookmark();
      const storedMarks = editor.state.storedMarks;
      const review = summaryReviewKey.getState(editor.state);
      keepReadingPosition(editor, () => {
        editor.view.updateState(baseline);
        replace(finalDocument, true);
        dispatch(closeHistory(editor.state.tr.setSelection(selection.resolve(editor.state.doc)).setStoredMarks(storedMarks)));
        if (reviewing && review) dispatch(reviewTransaction(editor.state.tr, { restore: { ...review, active: false, pending: [] } }));
      });
    },
    rollback() {
      if (finished) return;
      finished = true;
      const selection = hasUserChanges || hasUserSelection
        ? editor.state.selection.getBookmark().map(documentMapping(editor.state.doc, baseline.doc)).resolve(baseline.doc)
        : baseline.selection;
      keepReadingPosition(editor, () => {
        editor.view.updateState(baseline);
        dispatch(editor.state.tr.setSelection(selection));
        if (reviewing) dispatch(reviewTransaction(editor.state.tr, { restore: emptySummaryReview() }));
      });
    },
  };
}

export function createSummaryPreviewBatch(apply: (markdown: string) => void, interval = 100) {
  let pending: string | undefined;
  let timer: ReturnType<typeof setTimeout> | undefined;
  const flush = () => {
    clearTimeout(timer); timer = undefined;
    const value = pending; pending = undefined;
    if (value !== undefined) apply(value);
  };
  return {
    push(markdown: string) { pending = markdown; timer ??= setTimeout(flush, interval); },
    flush,
    cancel() { clearTimeout(timer); timer = undefined; pending = undefined; },
  };
}
